import parsehtml from 'html-react-parser';
import { isSearchingForProducts } from '../../../helpers/is-searching-for-products';
import { ObjectTypeSelector } from '../../../components/ObjectTypeSelector';
import { useObjectTypeSelector } from '../../../hooks/useObjectTypeSelector';
import useQueryFilters from '../../../hooks/useQueryFilters';
import { setupColumns } from '../helpers/setupColumns';
import { modifyResults } from '../helpers/modifyResults';
import ViewChange from '../../../components/ViewChange';

const { useEffect, useState, useMemo, useCallback } = wp.element;

const ResultsBlock = ( { attributes } ) => {
	const {
		objectType,
		template,
		itemPerPage,
		showEmptyQueryPlaceholder,
		additionalPostTypesToShow = [],
		showVariationsOnlyForAttributes,
		disablePersonalization,
		showObjectTypeSelector,
		overrideTemplates,
		showFancyAnimations,
		promoteFeaturedContent,
		searchableTaxonomies = [],
		showViewPicker,
	} = attributes;
	const [ results, setResults ] = useState( [] );
	const [ isEmptyQuery, setIsEmptyQuery ] = useState( false );

	const [ columns, setColumns ] = useState( () => attributes.columns );
	const [ mobileColumns, setMobileColumns ] = useState( () => attributes.mobileColumns );

	const isSearchingProducts = isSearchingForProducts( [ objectType, ...( additionalPostTypesToShow || [] ) ] );

	// Sync columns state with attributes when they change in the editor
	useEffect( () => {
		setColumns( attributes.columns );
	}, [ attributes.columns ] );

	useEffect( () => {
		setMobileColumns( attributes.mobileColumns );
	}, [ attributes.mobileColumns ] );

	useEffect( () => {
		wp.hooks.addFilter( 'qalaElasticCore.filter.isPersonalizationEnabled', 'qalaElasticCoreResultsBlock', () => ! disablePersonalization );
		wp.hooks.addFilter( 'qalaElasticCore.filter.isCustomDisjunctiveSearch', 'qalaElasticCoreResultsBlock', () => isSearchingProducts && showVariationsOnlyForAttributes );

		return () => {
			wp.hooks.removeFilter( 'qalaElasticCore.filter.isPersonalizationEnabled', 'qalaElasticCoreResultsBlock' );
			wp.hooks.removeFilter( 'qalaElasticCore.filter.isCustomDisjunctiveSearch', 'qalaElasticCoreResultsBlock' );
		};
	}, [ disablePersonalization, isSearchingProducts, showVariationsOnlyForAttributes ] );

	/**
	 * Memoized default filters for a query.
	 * Prevents unnecessary re-renders when used in filter comparisons.
	 */
	const defaultFilters = useMemo( () => [
		{ blog_id: ( window?.QalaElasticCore?.currentBlogId || 1 ).toString() },
		{ object_type: ( attributes.additionalPostTypesToShow && attributes.additionalPostTypesToShow.length > 0 ) ? [ objectType, ...attributes.additionalPostTypesToShow ] : objectType },
		{ is_visible: 'true' },
	], [ objectType, attributes.additionalPostTypesToShow ] );

	/**
	 * Memoized default "none" filters for a query.
	 */
	const defaultFiltersNone = useMemo( () => [
		{ object_type: showVariationsOnlyForAttributes ? 'product_variation' : '' },
	], [ showVariationsOnlyForAttributes ] );

	/**
	 * A more complete setter for isEmptyQuery that allows other plugins to interact with it.
	 * Also sets global QalaElasticCore properties for backward compatibility.
	 *
	 * @param {boolean} value Value of isEmptyQuery
	 */
	const setIsEmptyQueryFull = useCallback( value => {
		const filteredValue = wp.hooks.applyFilters( 'qalaElasticCore.filter.isEmptyQuery', value, showEmptyQueryPlaceholder );
		setIsEmptyQuery( filteredValue );
		QalaElasticCore.isEmptyQuery = filteredValue;
		QalaElasticCore.showEmptyQueryPlaceholder = showEmptyQueryPlaceholder;
	}, [ showEmptyQueryPlaceholder ] );

	// Register query filters using custom hook
	useQueryFilters( {
		itemPerPage,
		showVariationsOnlyForAttributes,
		isSearchingProducts,
		promoteFeaturedContent,
		setIsEmptyQuery: setIsEmptyQueryFull,
		defaultFilters,
		defaultFiltersNone,
	} );

	const addLoadingClass = useCallback( () => {
		const loadedClass = 'qec-is-loading';
		const body = document.querySelector( 'body' );

		if ( ! body.classList.contains( loadedClass ) ) {
			body.classList.add( loadedClass );
		}
	}, [] );

	const addLoadedClass = useCallback( () => {
		const loadedClass = 'qec-loaded';
		const body = document.querySelector( 'body' );

		if ( ! body.classList.contains( loadedClass ) ) {
			body.classList.add( loadedClass );
		}
	}, [] );

	/**
	 * Initial query execution and result handler registration.
	 * Runs once on component mount and cleanup on unmount.
	 */
	useEffect( () => {
		addLoadingClass();

		// We need to call doQuery with query param as null to correctly set the default query if needed but only on the initial load.
		wp.hooks.doAction( 'qalaElasticCore.action.doQuery', { query: null, options: {} } );

		const resultsHandler = elasticResults => {
			if ( ! showFancyAnimations || ! document.startViewTransition ) {
				setResults( modifyResults( elasticResults ) );
			} else {
				document.startViewTransition( () => {
					setResults( modifyResults( elasticResults ) );
				} );
			}
			addLoadedClass();
		};

		wp.hooks.addAction( 'qalaElasticCore.action.onResult.results', 'qalaElasticCore', resultsHandler );

		return () => {
			wp.hooks.removeAction( 'qalaElasticCore.action.onResult.results', 'qalaElasticCore', resultsHandler );
		};
	}, [ addLoadingClass, addLoadedClass, showFancyAnimations ] );

	const {
		allObjectTypes,
		searchOnClick,
		specificObjectTypeTemplate,
	} = useObjectTypeSelector( {
		objectType,
		showObjectTypeSelector,
		template,
		overrideTemplates,
		usedObjectTypes: [
			...( additionalPostTypesToShow || [] ),
			... ( searchableTaxonomies || [] ),
			objectType,
		],
	} );

	// Memoize the compiled template function since the template path doesn't change
	const compiledTemplate = useMemo( () => {
		const compiledFn = wp.template( specificObjectTypeTemplate );
		return compiledFn;
	}, [ specificObjectTypeTemplate ] );

	// Memoize the rendered template only re-parsing when results or columns change
	const renderedTemplate = useMemo( () => {
		if ( ! results.length || ( showEmptyQueryPlaceholder && isEmptyQuery ) ) {
			return null;
		}

		const rendered = parsehtml( compiledTemplate( { results, attributes: setupColumns( attributes, columns, mobileColumns ) } ) );
		return rendered;
	}, [ results, showEmptyQueryPlaceholder, isEmptyQuery, compiledTemplate, attributes, columns, mobileColumns ] );

	if ( ! results.length ) {
		return parsehtml( wp.template( 'qec-no-results' )( { attributes } ) );
	}

	if ( showEmptyQueryPlaceholder && isEmptyQuery ) {
		return parsehtml( wp.template( 'qec-empty-query-placeholder' )( { attributes } ) );
	}

	return (
		<>
			{ showObjectTypeSelector && <ObjectTypeSelector allObjectTypes={ allObjectTypes } searchOnClick={ searchOnClick } /> }
			{ showViewPicker &&
			<ViewChange
				attributes={ attributes }
				setColumns={ setColumns }
				setMobileColumns={ setMobileColumns }
			/>
			}
			<div key={ specificObjectTypeTemplate }>
				{ renderedTemplate }
			</div>
		</>
	);
};

export default ResultsBlock;
